import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';

import Graph from 'components/Graph/Graph';
import {
  isValidDate,
  defaultDates,
  fillGapsWith,
} from 'helpers/presetDateHelpers';
import { paramsToString, paramsToObject } from 'helpers/paramHelpers';
import Loading from 'components/Loading/Loading';

import DateRangePicker from 'components/DateRangePicker/DateRangePicker';
import OutsideClickHandler from 'hooks/OutsideClickHandler';
import ActiveCampaign2 from '../ActiveCampaign2/ActiveCampaign2';

import colors from 'theme/Colors.module.scss';
import './Graph.scss';

import { Box, Tabs, TabList, Tab } from '@chakra-ui/react';

import {
  useGetPrintStatesQuery,
  useGetPrintedOrderStatesQuery,
} from 'modules/dashboard/dashboardApi';
import { useGetCampaignsQuery } from 'modules/campaigns/campaignsApi';
import { setDateRange } from 'modules/app/appSlice';
import { setDashParamsToState } from 'modules/dashboard/dashboardSlice';

// function zip(...arrays) {
//   const length = Math.min(...arrays.map(arr => arr.length));
//   return Array.from({ length }, (_, i) => arrays.map(arr => arr[i]));
// }

function formatNumber(number) {
  if (number < 1000) {
    return number.toString(); // Return the number as is if it's less than 1000
  } else {
    const formattedNumber = (number / 1000).toFixed(1);
    return `${formattedNumber}k`;
  }
}

export const tabOptions = [
  { label: 'Personalized Prints', value: 'prints' },
  { label: 'Personalized Packages', value: 'packages' },
];

const generateRange = (startDate, endDate) => ({
  start: startDate.local().format('YYYY-MM-DD'),
  startUtc: startDate.startOf('day').utc().format(),
  startRaw: startDate.local(),
  end: endDate.local().format('YYYY-MM-DD'),
  endUtc: endDate.endOf('day').utc().format(),
  endRaw: endDate.local(),
  days: endDate.diff(startDate, 'days'),
});

const GraphDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const rangePickerRef = useRef(null);

  // States
  const [dateRangeDropdownVisible, setDateRangeDropdownVisible] =
    useState(false);
  const [isCustomRangePickerVisible, setIsCustomRangePickerVisible] =
    useState(false);
  const [tabOption, setTabOption] = useState(tabOptions[0]);
  const [printDataPoints, setPrintDataPoints] = useState([]);
  const [printedOrderDataPoints, setPrintedOrderDataPoints] = useState([]);

  const { data: campaignsData, isLoading } = useGetCampaignsQuery({
    current_page: 1,
    size: 500,
  });

  // Selectors
  const dashParamsFromState = useSelector(
    state => state.dashboard.dashParamsFromState
  ); // Access stored dashboard data
  const dateRange = useSelector(state => state.app.dateRange); // Access stored user data

  // Dates Calculation
  const { start: userStart, end: userEnd } = paramsToObject(
    dashParamsFromState || search
  );

  const { from, to } = dateRange;
  const range = generateRange(moment(from), moment(to));

  const getInitialDateState = () => {
    return {
      from: userStart
        ? new Date(moment(userStart))
        : new Date(defaultDates.start),
      to: userEnd ? new Date(moment(userEnd)) : new Date(defaultDates.end),
    };
  };

  const params = paramsToString({
    start: range.start,
    end: range.end,
  });

  const utcParams = paramsToString({
    start: range.startUtc,
    end: range.endUtc,
    tz: moment.tz(moment.tz.guess()).zoneAbbr(),
    all_campaigns: 0,
    show_by_day: 1,
  });

  const { data: printStatesData, isLoading: printStatesDataIsLoading } =
    useGetPrintStatesQuery(utcParams, {
      skip:
        !isValidDate(moment(from)) ||
        !isValidDate(moment(to)) ||
        dateRange?.length === 0,
    });

  const {
    data: printedOrderStatesData,
    isLoading: printedOrderStatesDataIsLoading,
  } = useGetPrintedOrderStatesQuery(utcParams, {
    skip:
      !isValidDate(moment(from)) ||
      !isValidDate(moment(to)) ||
      dateRange?.length === 0,
  });

  // Stats
  let totalPrints = printStatesData?.data.reduce((a, b) => a + b[1], 0);
  let totalPackage = printedOrderStatesData?.data.reduce((a, b) => a + b[1], 0);

  // Initialization
  useEffect(() => {
    if (dateRange?.length === 0) {
      dispatch(setDateRange(getInitialDateState()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isValidDate(moment(from)) &&
      isValidDate(moment(to)) &&
      dateRange?.length !== 0
    ) {
      dispatch(setDashParamsToState(params));
      navigate(`/dashboard${params}`);
    } else {
      dispatch(setDateRange(getInitialDateState()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  // Generate Data Points
  const printData = stats => {
    const printMap = new Map();

    // group data by campaign id
    if (stats) {
      stats.data.forEach(data => {
        if (printMap.has(data[0])) {
          printMap.get(data[0]).push(data);
        } else {
          printMap.set(data[0], [data], 0);
        }
      });
    }

    const groupPrintData = Array.from(printMap, ([id, data]) => ({ id, data }));

    // generate total_imp
    groupPrintData.forEach(campaignData => {
      let total_imp = 0;
      campaignData.data.forEach(item => (total_imp += item[1]));
      campaignData.total_imp = total_imp;
    });

    return groupPrintData;
  };

  const generateDataPoints = (stats, statsType) => {
    let acc = [];

    stats &&
      printData(stats).forEach(campaign => {
        const dataPoints = campaign.data;
        dataPoints.forEach(dataPoint => {
          const data = parseFloat(dataPoint[1]);
          let dt = moment(dataPoint[2]).format('YYYY-MM-DD');
          acc[dt] = acc[dt] ? (acc[dt] += data) : data;
        });
      });
    if (acc[range.start] === undefined) acc[range.start] = 0;
    if (acc[range.end] === undefined) acc[range.end] = 0;
    acc = fillGapsWith(acc, 0);
    acc = Object.fromEntries(Object.entries(acc).sort());

    if (statsType === 'print') {
      setPrintDataPoints(acc);
    }
    if (statsType === 'printedOrder') {
      setPrintedOrderDataPoints(acc);
    }
  };

  useEffect(() => {
    generateDataPoints(printStatesData, 'print');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printStatesData]);

  useEffect(() => {
    generateDataPoints(printedOrderStatesData, 'printedOrder');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printedOrderStatesData]);

  // Filter Campaigns
  const filteredCampaigns = (campaigns, stats) => {
    const campaignIdsInRange = printData(stats).map(stat => stat.id);
    const filtered = campaigns?.filter(campaign =>
      campaignIdsInRange.includes(campaign.id)
    );
    return filtered;
  };

  // Build Data
  const buildData = (dataPoints, dataType) => {
    const values = Object.values(dataPoints);
    const dataSets = {
      title:
        dataType === 'print' ? 'Personalized Prints' : 'Personalized Packages',
      values,
      total: '',
      color: colors.primary100,
      hoverColor: colors.primary900,
      stack: 'Base',
      type: 'bar',
    };
    return dataSets;
  };

  const dataSets = (dataPoints, dataType) => {
    return {
      data: [buildData(dataPoints, dataType)],
      xLabels: Object.keys(dataPoints),
    };
  };

  let tooltipEl; // singleton outside chartOptions

  // Chart Options
  const chartOptions = (dataPoints, dataType) => {
    const { xLabels, data } = dataSets(dataPoints, dataType); // Call once

    // Zip once
    const zippedAry = xLabels.map((label, i) => [
      label,
      data[0]?.values[i] ?? 0,
    ]);

    return {
      legend: { display: false },

      tooltips: {
        // Disable the on-canvas tooltip
        enabled: false,
        custom: function (tooltipModel) {
          // Tooltip Element
          // Create element on first render
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.className = 'chartjs-tooltip';
            document.body.appendChild(tooltipEl);
          }

          // Hide if no tooltip
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          const dateString = tooltipModel.title?.[0];
          const curIndex = zippedAry.findIndex(
            ([label]) => label === dateString
          );

          const curValue = Number(zippedAry[curIndex]?.[1] ?? 0);
          const prevValue = Number(zippedAry[curIndex - 1]?.[1] ?? 0);
          const diffPercent =
            prevValue !== 0 ? ((curValue - prevValue) / prevValue) * 100 : 100;
          const formattedPercent = `${
            diffPercent >= 0 ? '+' : ''
          }${diffPercent.toFixed(2)}%`;

          tooltipEl.innerHTML = `
          <span class="tooltip-prints">${curValue.toLocaleString()} (${formattedPercent})</span>
        `;

          // `this` will be the overall tooltip
          const position = this._chart.canvas.getBoundingClientRect();
          tooltipEl.style.opacity = 1;
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.left = `${
            position.left + window.pageXOffset + tooltipModel.caretX
          }px`;
          tooltipEl.style.top = `${
            position.top + window.pageYOffset + tooltipModel.caretY - 80
          }px`;
          tooltipEl.style.pointerEvents = 'none';

          tooltipEl.className = `chartjs-tooltip ${
            tooltipModel.yAlign || 'no-transform'
          }`;
        },
      },

      scales: {
        xAxes: [
          {
            ticks: {
              beginAtZero: true,
              userCallback: value => moment(value).format('MMM DD'),
            },
            gridLines: { display: false },
          },
        ],
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              userCallback: value => formatNumber(value),
            },
            stacked: false,
          },
        ],
      },
    };
  };

  if (
    isLoading ||
    printStatesDataIsLoading ||
    printedOrderStatesDataIsLoading
  ) {
    return <Loading type="fullpage" height={`calc(100vh - 350px)`} />;
  }

  return (
    <Box className="dashboard-graph">
      <Box className="dashboard-graph__top-container">
        <Box className="dashboard-graph__tabs-wrapper">
          <Tabs className="dashboard-graph__tabs" variant="unstyled">
            <TabList className="dashboard-graph__tab-list">
              <Tab
                className="dashboard-graph__tab"
                onClick={() => {
                  setTabOption(tabOptions[0]);
                }}
                _selected={{ color: colors.white, bg: colors.primary900 }}
              >
                <span className="dashboard-graph__tab-text">
                  {`Personalized Prints ( ${Number(totalPrints).toLocaleString(
                    'en-US'
                  )} total )`}
                </span>
              </Tab>

              <Tab
                className="dashboard-graph__tab"
                onClick={() => {
                  setTabOption(tabOptions[1]);
                }}
                _selected={{ color: colors.white, bg: colors.secondary900 }}
              >
                <span className="dashboard-graph__tab-text">
                  {`Personalized Packages ( ${Number(
                    totalPackage
                  ).toLocaleString('en-US')} total )`}
                </span>
              </Tab>
            </TabList>
          </Tabs>
        </Box>
        <Box className="dashboard-graph__date-range-wrapper">
          <OutsideClickHandler
            onHandler={(e, ref) => {
              setDateRangeDropdownVisible(false);
              setIsCustomRangePickerVisible(false);
            }}
          >
            <DateRangePicker
              isCustomRangePickerVisible={isCustomRangePickerVisible}
              setIsCustomRangePickerVisible={setIsCustomRangePickerVisible}
              dateRangeDropdownVisible={dateRangeDropdownVisible}
              setDateRangeDropdownVisible={setDateRangeDropdownVisible}
              ref={rangePickerRef}
              date={{ from, to }}
            />
          </OutsideClickHandler>
        </Box>
      </Box>

      {tabOption.value === 'prints' && (
        <Box minHeight="400px">
          <Graph
            dataSet={dataSets(printDataPoints, 'print')}
            customOptions={chartOptions(printDataPoints, 'print')}
            tabOption={tabOption}
          />
        </Box>
      )}
      {tabOption.value === 'packages' && (
        <Box minHeight="400px">
          <Graph
            dataSet={dataSets(printedOrderDataPoints, 'printedOrder')}
            customOptions={chartOptions(printedOrderDataPoints, 'printedOrder')}
            tabOption={tabOption}
          />
        </Box>
      )}

      {tabOption.value === 'prints' && (
        <Box minHeight="250px">
          <ActiveCampaign2
            campaigns={filteredCampaigns(
              campaignsData?.data.entities,
              printStatesData
            )}
            stats={printData(printStatesData)}
            tabOption={tabOption}
          />
        </Box>
      )}
    </Box>
  );
};

export default GraphDashboard;
